import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Row count below which windowing stays off — small lists render whole so
 * browser find-in-page and screen-reader row counts keep working.
 */
export const WINDOW_THRESHOLD = 120;

/** Rows kept mounted above and below the viewport to hide scroll tearing. */
const OVERSCAN = 12;

/** Starting guess for row height; replaced by a measurement on first paint. */
const DEFAULT_ROW_HEIGHT = 33;

export interface WindowedRows {
  /** Index of the first row to render. */
  start: number;
  /** Index one past the last row to render. */
  end: number;
  /** Filler height standing in for the rows above `start`, in pixels. */
  padTop: number;
  /** Filler height standing in for the rows below `end`, in pixels. */
  padBottom: number;
  /** Whether windowing is active — false means every row is rendered. */
  active: boolean;
}

/**
 * Windows a page-scrolled table body: only the rows near the viewport are
 * rendered, with spacer rows standing in for the rest.
 *
 * The table stays a real `<table>` — `content-visibility` is not an option
 * here, because size containment does not apply to internal table elements —
 * so the scroll extent is reproduced with two padding rows instead.
 *
 * Row height is averaged over every rendered row rather than assumed or taken
 * from a single sample. Cells that wrap (a Route with several hosts, a long
 * name breaking across lines) make individual rows differ, and averaging over
 * the window keeps the estimate stable instead of oscillating between the
 * tall and short rows that happen to be on screen.
 *
 * Rows the window excludes are absent from the DOM, so find-in-page only sees
 * what is rendered. That is why windowing stays off below WINDOW_THRESHOLD,
 * and why the name filter is the intended way to search a long list.
 */
export function useWindowedRows(
  count: number,
  bodyRef: RefObject<HTMLElement>,
  enabled = true,
): WindowedRows {
  const active = enabled && count > WINDOW_THRESHOLD;
  const [rowHeight, setRowHeight] = useState(DEFAULT_ROW_HEIGHT);
  // Only the window's first index is state. The last is derived at render
  // time from the viewport, so a list that grows never paints an empty body
  // while it waits for the scroll effect to catch up.
  const [start, setStart] = useState(0);
  const [viewport, setViewport] = useState(() =>
    typeof window === 'undefined' ? 900 : window.innerHeight,
  );
  const frameRef = useRef(0);

  useEffect(() => {
    if (!active) {
      setStart(0);
      return undefined;
    }

    const recompute = () => {
      const body = bodyRef.current;
      if (!body) return;

      // Average height of the rows currently laid out, spacers excluded.
      const rendered = body.querySelectorAll<HTMLElement>(
        'tr:not([aria-hidden])',
      );
      if (rendered.length > 1) {
        const top = rendered[0].getBoundingClientRect().top;
        const bottom =
          rendered[rendered.length - 1].getBoundingClientRect().bottom;
        const measured = (bottom - top) / rendered.length;
        if (measured > 1) {
          setRowHeight(current =>
            Math.abs(current - measured) > 0.5 ? measured : current,
          );
        }
      }

      // How far the body's top has travelled above the viewport. Reading the
      // live rect makes this work under page scroll, a scrolling ancestor, or
      // both at once.
      const scrolledPast = Math.max(0, -body.getBoundingClientRect().top);
      const first = Math.max(
        0,
        Math.floor(scrolledPast / rowHeight) - OVERSCAN,
      );
      setStart(current => (current === first ? current : first));
      setViewport(current =>
        current === window.innerHeight ? current : window.innerHeight,
      );
    };

    const schedule = () => {
      if (frameRef.current) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0;
        recompute();
      });
    };

    recompute();
    window.addEventListener('scroll', schedule, {
      passive: true,
      capture: true,
    });
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    };
    // `count` is not read here, but a changed list means the body moved or
    // resized — re-running gives it a fresh recompute.
  }, [active, bodyRef, count, rowHeight]);

  if (!active) {
    return { start: 0, end: count, padTop: 0, padBottom: 0, active: false };
  }

  const windowSize = Math.ceil(viewport / rowHeight) + OVERSCAN * 2;
  const first = Math.min(start, Math.max(0, count - 1));
  const last = Math.min(count, first + windowSize);
  return {
    start: first,
    end: last,
    padTop: first * rowHeight,
    padBottom: Math.max(0, count - last) * rowHeight,
    active: true,
  };
}
